import { CalendarDays, MessageCircle, Pencil, Plus } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { eventDetailPath } from '../event-discovery/constants';
import { MESSAGE_ACTION_LABEL, threadPath } from '../messages/constants';

import { ConnectionNoteDialog } from './connection-note-dialog';
import {
  connectionPath,
  eventPillAccessibleName,
  initialsOf,
  noteButtonAccessibleName,
  noteButtonLabel,
  personName,
  roleAtCompany,
} from './constants';
import type { Connection } from './types';

/**
 * One card in the connections grid — `Web App.dc.html` §3.15.
 *
 * ```
 * card    min-width:0; --surface-card; 1px --border-subtle; --radius-lg; --shadow-xs; padding:18px
 * head    46px avatar, name over role, both clickable
 * pill    event, 9px uppercase, --purple-50 / --brand-primary, max-width:100%, left aligned
 * note    optional, italic, --surface-sunken, --radius-md, 11px 13px
 * actions gap 9: the note button
 * ```
 *
 * ## What replaced the row this used to be
 *
 * A single-column list row with a *Connected* pill and a chevron, transcribed from the phone
 * prototype. The 10 September design is one app at every width (Sumit, 2026-09-11) and draws this
 * as a two-column grid of cards; the pill went with it, because every card on the screen is a
 * connection and a badge that is always the same says nothing. The event travels on the card as
 * the prototype's pill, which is `MEM-033` AC-6 and is the whole reason a cross-event list is
 * readable three weeks later.
 *
 * ## The three targets, and why they are three elements rather than one card-wide link
 *
 * The head opens the connection (`MEM-033` AC-2), the event pill opens the event, and the note
 * button opens the composer. A card-wide `<Link>` with controls inside it would nest interactive
 * elements, which is invalid and is announced wrongly (NFR accessibility#wcag-aa) — the prototype
 * puts three handlers on one card and this is the same three, each a real focus stop with a real
 * accessible name.
 *
 * ## The note is on the card, and it is the reader's own
 *
 * `GET /api/connections` carries it since 2026-09-11 — see
 * `apps/api/src/domains/connections/types.ts`'s header for the change and why `MEM-033` AC-4 is
 * untouched by it. It is the member's own note about the other member, on the member's own screen,
 * and the button beside it has to know whether one exists to read *Add note* or *Edit note*.
 *
 * ## §3.15's Messages control, built (`MEM-054` AC-2)
 *
 * At the left of the action row, taking the width, with the note button beside it — the
 * prototype's own proportions. It opens the pair's thread at `/app/messages/{ref}`, which is the
 * **same handle** the card already carries: a thread and the connection it belongs to are one
 * relationship, and `MEM-025` AC-7's lesson is that naming it twice is how a member ends up with
 * two of something they have one of.
 *
 * **The prototype's unread mail glyph on this control is not built**, and its absence is the
 * correction rather than an omission. §3.15 draws a fuchsia `mail` beside *Messages* when the
 * thread is unread — but the connections list carries no unread state, and giving it one would
 * mean either a second count on a payload that has no use for one or a second request per card.
 * Unread is the Messages page's and the navigation dot's (`MEM-055` AC-3, AC-4), which is where a
 * member goes to find out what is waiting. Listed in the pull request.
 */
export function ConnectionCard({
  connection,
}: {
  readonly connection: Connection;
}): React.ReactElement {
  const [composing, setComposing] = React.useState(false);
  const name = personName(connection);
  const line = roleAtCompany(connection);
  // `?? ''` rather than `=== null`: the wire types here are hand-written mirrors, and
  // `event-discovery/types.ts` records the rule this file follows — treat every optional as
  // genuinely optional, because a payload that stops sending a field renders `undefined` rather
  // than failing a build. A card that threw on it would take the whole grid down with it.
  const note = (connection.note ?? '').trim().length === 0 ? null : connection.note;

  return (
    <article className="flex h-full min-w-0 flex-col gap-3 rounded-lg border border-border-subtle bg-card p-[18px] shadow-xs">
      <Link
        to={connectionPath(connection)}
        className="flex min-w-0 items-center gap-[13px] rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      >
        <span
          className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-pill bg-brand-100 font-brand text-[15px] font-bold text-brand-700"
          aria-hidden
        >
          {initialsOf(connection)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-brand text-md font-bold text-foreground">
            {name}
          </span>
          {line === null ? null : (
            <span className="block truncate text-sm text-muted-foreground">{line}</span>
          )}
        </span>
      </Link>

      {/* `MEM-033` AC-6's origin event, as the prototype's pill — a control rather than a label,
          because the prototype's is one and because the event is where the rest of the evening
          is. `self-start` so it takes its own width instead of the card's. */}
      {/* Both nulls mean "draw no pill", and they mean different things: a profile-code connection
          never had an event (`MEM-057` AC-4), and a resolved one whose event has gone has an id
          the member cannot usefully follow. Neither gets a label saying so — an absent pill is the
          prototype's own treatment for a card with nothing to say about where. */}
      {connection.eventName === null || connection.eventId === null ? null : (
        <Link
          to={eventDetailPath(connection.eventId)}
          aria-label={eventPillAccessibleName(connection.eventName)}
          className="inline-flex max-w-full items-center gap-1.5 self-start rounded-pill bg-brand-50 px-[11px] py-[5px] font-brand text-[9px] font-bold uppercase leading-[1.3] tracking-[0.05em] text-primary"
        >
          <CalendarDays className="h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate">{connection.eventName}</span>
        </Link>
      )}

      {note === null ? null : (
        <p className="rounded-md bg-muted px-[13px] py-[11px] text-sm italic leading-snug text-text-body">
          {note}
        </p>
      )}

      <div className="mt-auto flex gap-[9px] pt-1">
        {/* §3.15's Messages button: `flex:1`, secondary outline, a muted `message-circle`. */}
        <Link
          to={threadPath(connection.connectionRef)}
          aria-label={`${MESSAGE_ACTION_LABEL} ${name}`}
          className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-pill border-[1.5px] border-border bg-card p-2.5 font-brand text-sm font-bold text-text-body transition-colors hover:border-primary hover:text-primary"
        >
          <MessageCircle className="h-[15px] w-[15px] text-muted-foreground" aria-hidden />
          {MESSAGE_ACTION_LABEL}
        </Link>
        <button
          type="button"
          aria-label={noteButtonAccessibleName({ note: connection.note, name })}
          onClick={() => {
            setComposing(true);
          }}
          className="inline-flex flex-none items-center justify-center gap-1.5 rounded-pill border-[1.5px] border-border bg-card px-[15px] py-2.5 font-brand text-sm font-bold text-text-body transition-colors hover:border-primary hover:text-primary"
        >
          {note === null ? (
            <Plus className="h-[15px] w-[15px]" aria-hidden />
          ) : (
            <Pencil className="h-[15px] w-[15px]" aria-hidden />
          )}
          {noteButtonLabel(connection.note)}
        </button>
      </div>

      <ConnectionNoteDialog
        open={composing}
        onOpenChange={setComposing}
        connectionRef={connection.connectionRef}
        name={name}
        note={connection.note}
      />
    </article>
  );
}
